import { useEffect, useRef } from 'react';

import { AudioPlayer } from './audio/audio-player';
import { AudioTranscript } from './audio/audio-transcript';
import { useAudioPlayback } from './audio/use-audio-playback';
import { useGetDocumentAudioBlocks, useGetDocumentAudioUrl } from '@/api/queries/useGetDocumentAudio';
import { TimeRangeWithId } from '@/types';

/** Stable default so the transcript's highlight memos don't recompute on every render. */
const EMPTY_TIME_HIGHLIGHTS: TimeRangeWithId[] = [];

export type AudioFileViewerSize = 'default' | 'sm';

interface AudioFileViewerProps {
  matterId: string;
  docId: string;
  size?: AudioFileViewerSize;
  timeHighlights?: TimeRangeWithId[];
  highlightedFactId?: string | null;
  onHighlightClick?: (id: string) => void;
  onClose?: () => void;
}

export function AudioFileViewer({
  matterId,
  docId,
  size = 'default',
  timeHighlights = EMPTY_TIME_HIGHLIGHTS,
  highlightedFactId,
  onHighlightClick,
  onClose,
}: AudioFileViewerProps) {
  const {
    data: audioUrl,
    isLoading: isAudioUrlLoading,
    isError: isAudioUrlError,
    refetch: refetchAudioUrl,
  } = useGetDocumentAudioUrl(matterId, docId);

  const playback = useAudioPlayback({ audioUrl, refetchAudioUrl: () => void refetchAudioUrl() });

  /** Space toggles playback whenever focus is inside the viewer, rather than re-activating a focused block. */
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== ' ') return;
    if ((event.target as HTMLElement).closest('input, textarea, [contenteditable="true"]')) return;

    event.preventDefault();
    void playback.togglePlayback();
  };

  const {
    data: blocks,
    isLoading: areBlocksLoading,
    isError: areBlocksError,
  } = useGetDocumentAudioBlocks(matterId, docId);

  const { seekTo, duration } = playback;
  const seekedFactKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!highlightedFactId) {
      seekedFactKeyRef.current = null;
      return;
    }

    if (!duration) return;

    const seekKey = `${docId}:${highlightedFactId}`;
    if (seekedFactKeyRef.current === seekKey) return;

    const firstRange = timeHighlights
      .filter((highlight) => highlight.id === highlightedFactId)
      .sort((a, b) => a.startSeconds - b.startSeconds)[0];

    if (!firstRange) {
      // Set to null just in case, although unlikely we ever get an audio reference without any time
      // ranges
      seekedFactKeyRef.current = null;
      return;
    }

    seekedFactKeyRef.current = seekKey;
    seekTo(firstRange.startSeconds);
  }, [docId, highlightedFactId, timeHighlights, duration, seekTo]);

  if (isAudioUrlError || areBlocksError) {
    return (
      <div className={`flex h-full items-center justify-center text-gray-600 ${size === 'sm' ? 'text-xs' : 'text-sm'}`}>
        Audio or transcript could not be loaded.
      </div>
    );
  }

  return (
    <div className="relative flex h-full min-h-0 flex-col bg-white" onKeyDown={handleKeyDown}>
      <AudioPlayer
        matterId={matterId}
        docId={docId}
        isAudioUrlLoading={isAudioUrlLoading}
        playback={playback}
        onClose={onClose}
        size={size}
      />

      <AudioTranscript
        blocks={blocks}
        isLoading={areBlocksLoading}
        currentTime={playback.currentTime}
        isPlaying={playback.isPlaying}
        seekedTo={playback.seekedTo}
        onSeek={playback.seekTo}
        timeHighlights={timeHighlights}
        highlightedFactId={highlightedFactId}
        onHighlightClick={onHighlightClick}
        size={size}
      />
    </div>
  );
}
